import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TermsOfService from '../../components/TermsOfService';
import { legalService, PLATFORM_SERVICE_CONTRACT_KEY } from '../../services/legalService';

const ContractSignaturePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signed, setSigned] = useState(false);
  const [data, setData] = useState<any>(null);
  const [signatureName, setSignatureName] = useState('');
  const [contractViewed, setContractViewed] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState<{ key: string; version: string }[]>([]);

  useEffect(() => {
    if (!token) {
      setError('Link inválido');
      setLoading(false);
      return;
    }
    legalService
      .getPublicSignatureRequest(token)
      .then((payload) => {
        if (payload?.status === 'signed') {
          setSigned(true);
        }
        setData(payload);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'Não foi possível abrir esta solicitação.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleAcceptTerms = async () => {
    try {
      const template = await legalService.getTemplate(data?.templateKey || PLATFORM_SERVICE_CONTRACT_KEY);
      setLegalAccepted([{ key: template.key, version: template.version }]);
    } catch {
      /* backend valida */
    }
    setContractViewed(true);
    setAccepted(true);
    setShowTerms(false);
  };

  const handleSign = async () => {
    if (!token) return;
    if (!contractViewed || !accepted) {
      setError('Abra e leia o contrato antes de assinar.');
      return;
    }
    if (!signatureName.trim()) {
      setError('Informe o nome de quem assina.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await legalService.signPublicSignatureRequest(token, {
        signatureName: signatureName.trim(),
        contractViewed: true,
        legalAccepted,
      });
      setSigned(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Não foi possível assinar.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (signed) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Contrato assinado
          </Typography>
          <Typography color="text.secondary">
            A assinatura eletrônica foi registrada. Você também pode baixar o PDF em Configurações, no painel da organização.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Assinatura de contrato
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {data?.parishName} · {data?.template?.title || 'Contrato'} · versão {data?.templateVersion}
        </Typography>
        {data?.note && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {data.note}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Button variant="outlined" onClick={() => setShowTerms(true)}>
          Ler contrato
        </Button>
        <TextField
          fullWidth
          sx={{ mt: 3 }}
          label="Nome de quem assina"
          value={signatureName}
          onChange={(e) => setSignatureName(e.target.value)}
        />
        <FormControlLabel
          sx={{ mt: 2, display: 'block' }}
          control={
            <Checkbox
              checked={accepted}
              onChange={(e) => {
                if (!contractViewed) {
                  setError('Abra e leia o contrato antes de aceitar.');
                  return;
                }
                setAccepted(e.target.checked);
              }}
            />
          }
          label="Li e aceito o contrato de prestação de serviços"
        />
        <Button fullWidth variant="contained" sx={{ mt: 3 }} disabled={submitting} onClick={handleSign}>
          {submitting ? <CircularProgress size={22} /> : 'Assinar eletronicamente'}
        </Button>
      </Paper>
      <TermsOfService
        open={showTerms}
        onClose={() => setShowTerms(false)}
        onAccept={handleAcceptTerms}
        parishName={data?.parishName}
        parishAddress={data?.parishAddress}
        parishCity={data?.parishCity}
        parishState={data?.parishState}
        parishCNPJ={data?.parishCnpj}
        responsibleName={signatureName}
        responsibleEmail={data?.parishEmail}
      />
    </Container>
  );
};

export default ContractSignaturePage;
